'use client'

import { toastReducer } from '@trustoo/components/ui/toaster/funcs/toastReducer'
import type { ToastAction } from '@trustoo/components/ui/toaster/funcs/toastReducer'
import type { ToastState } from '@trustoo/components/ui/toaster/types/toastTypes'
import { useCallback, useSyncExternalStore } from 'react'

/**
 * Per-`BaseModal` toast channels, keyed by NiceModal id.
 *
 * This is a module-level store rather than a React context on purpose: the component that
 * renders a `BaseModal` (the `NiceModal.create` wrapper) sits above the card's own context, and
 * both sides already share the NiceModal id via `useModal()`. Keying on it needs no provider, no
 * provider-order rule, and lets a toast re-render only the card it belongs to.
 *
 * A channel exists only while its `BaseModal` is mounted; writes to an unregistered id are
 * dropped. Two React roots showing the same NiceModal id at the same time would share a channel,
 * which is accepted for a client-only UI concern.
 */
const maxToasts = 1
const noToasts: ToastState = {}
const toastsByModal = new Map<string, ToastState>()
const listenersByModal = new Map<string, Set<() => void>>()

/** Opens the channel for a `BaseModal`. Returns the cleanup that closes it, for use in an effect. */
export const registerBaseModalToastChannel = (modalId: string): (() => void) => {
    if (!toastsByModal.has(modalId)) {
        toastsByModal.set(modalId, noToasts)
    }
    return () => {
        toastsByModal.delete(modalId)
        listenersByModal.get(modalId)?.forEach((listener) => listener())
    }
}

export const dispatchBaseModalToast = (modalId: string, action: ToastAction) => {
    const toasts = toastsByModal.get(modalId)
    if (toasts === undefined) {
        return
    }

    const next = toastReducer(toasts, action.type === 'add' ? { ...action, maxToasts } : action)
    if (next === toasts) {
        return
    }

    toastsByModal.set(modalId, next)
    listenersByModal.get(modalId)?.forEach((listener) => listener())
}

/** Subscribes to one channel; only the `BaseModal` outlet rendering the toasts needs this. */
export const useBaseModalToasts = (modalId: string): ToastState => {
    const subscribe = useCallback(
        (listener: () => void) => {
            const listeners = listenersByModal.get(modalId) ?? new Set<() => void>()
            listeners.add(listener)
            listenersByModal.set(modalId, listeners)
            return () => {
                listeners.delete(listener)
                if (listeners.size === 0) {
                    listenersByModal.delete(modalId)
                }
            }
        },
        [modalId],
    )

    return useSyncExternalStore(
        subscribe,
        () => toastsByModal.get(modalId) ?? noToasts,
        () => noToasts,
    )
}
